import { useMemo, useState, type ReactElement } from 'react';
import { DEFAULT_LIST_PAGE_SIZE } from '../../constants';
import { paginateRows } from '../../domain/contracts';
import { GroupedDataGrid, type GroupedColumnDef } from '../grid/GroupedDataGrid';
import { GridPaginationBar } from '../grids/GridPaginationBar';
import { EntityActivitySection } from '../activity/EntityActivitySection';
import { FilterTiles } from './FilterTiles';
import { MasterRecordPanel } from './MasterRecordPanel';
import { MasterSearchField } from './searchField';

type AuthFetch = (url: string, init?: RequestInit) => Promise<Response>;

export interface FieldRow {
  id: string;
  name: string;
  state: string;
  operatingRegion: string;
  recoveryType: string;
}

const COLUMNS: GroupedColumnDef[] = [
  { id: 'name', label: 'Name', header: 'Name' },
  { id: 'state', label: 'State', header: 'State' },
  { id: 'region', label: 'Region', header: 'Region' },
  { id: 'recovery', label: 'Recovery', header: 'Recovery' }
];

function groupValue(row: FieldRow, columnId: string): string {
  switch (columnId) {
    case 'name':
      return row.name;
    case 'state':
      return row.state;
    case 'region':
      return row.operatingRegion;
    case 'recovery':
      return row.recoveryType;
    default:
      return '—';
  }
}

export function FieldDetail({
  item,
  variant = 'panel',
  onClose,
  onBack,
  authFetch
}: {
  item: FieldRow;
  variant?: 'panel' | 'page';
  onClose?: () => void;
  onBack?: () => void;
  authFetch?: AuthFetch;
}): ReactElement {
  return (
    <MasterRecordPanel
      testId="field-side-panel"
      ariaLabel="Field details"
      title={item.name}
      status={item.recoveryType}
      variant={variant}
      onClose={onClose}
      onBack={onBack}
    >
      <dl className="detail-list">
        <dt>Name</dt>
        <dd>{item.name}</dd>
        <dt>State</dt>
        <dd className="mono-id">{item.state}</dd>
        <dt>Region</dt>
        <dd>{item.operatingRegion}</dd>
        <dt>Recovery</dt>
        <dd>
          <span className="status-pill">{item.recoveryType}</span>
        </dd>
      </dl>
      {authFetch && (
        <EntityActivitySection authFetch={authFetch} path={`/fields/${item.id}/activity`} testId="field-activity" />
      )}
    </MasterRecordPanel>
  );
}

export function FieldsRegister({
  rows,
  query,
  onQuery,
  onOpen,
  authFetch
}: {
  rows: FieldRow[];
  query: string;
  onQuery: (value: string) => void;
  onOpen?: (id: string) => void;
  authFetch?: AuthFetch;
}): ReactElement {
  const [filter, setFilter] = useState('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);

  const regions = useMemo(() => [...new Set(rows.map((r) => r.operatingRegion).filter(Boolean))], [rows]);
  const filtered = useMemo(
    () => (filter === 'all' ? rows : rows.filter((r) => r.operatingRegion === filter)),
    [filter, rows]
  );
  const pageRows = useMemo(() => paginateRows(filtered, page, pageSize), [filtered, page, pageSize]);
  const selected = rows.find((r) => r.id === selectedId) ?? null;

  return (
    <>
      <FilterTiles
        testId="field-tiles"
        value={filter}
        onChange={(next) => {
          setFilter(next);
          setPage(1);
          setSelectedId(null);
        }}
        options={[
          { id: 'all', label: 'All', count: rows.length },
          ...regions.map((region) => ({
            id: region,
            label: region,
            count: rows.filter((r) => r.operatingRegion === region).length
          }))
        ]}
      />
      <div className={`requests-shell${selectedId ? ' has-panel' : ''}`}>
        <section className="card requests-grid-card" data-testid="fields-grid">
          <div className="row spread requests-grid-toolbar">
            <h2>Fields</h2>
          </div>
          <p className="hint">Click a row for the side panel; double-click to open the full page.</p>
          <MasterSearchField value={query} onChange={onQuery} />
          <GroupedDataGrid<FieldRow>
            columns={COLUMNS}
            rows={pageRows}
            getRowId={(r) => r.id}
            getGroupValue={groupValue}
            colSpan={4}
            emptyMessage="No fields match this status."
            groupHint="Click a column title to sort. Drag the grip beside a title into the strip to group."
            renderRow={(row) => (
              <tr
                className={selectedId === row.id ? 'selected' : undefined}
                data-testid="field-row"
                onClick={() => setSelectedId(row.id)}
                onDoubleClick={(e) => {
                  e.preventDefault();
                  onOpen?.(row.id);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedId(row.id);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <td>{row.name}</td>
                <td className="mono-id">{row.state}</td>
                <td>{row.operatingRegion}</td>
                <td>
                  <span className="status-pill">{row.recoveryType}</span>
                </td>
              </tr>
            )}
          />
          <GridPaginationBar
            idPrefix="fields"
            page={page}
            pageSize={pageSize}
            totalCount={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </section>
        {selected && <FieldDetail item={selected} authFetch={authFetch} onClose={() => setSelectedId(null)} />}
      </div>
    </>
  );
}
